// ============================================================
// Hirafi — Module devis : hook devis standalone (connecté)
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import type { StandaloneDevisFormData } from "../types";
import type { Quote } from "@/hooks/useQuotes";

/** Devis standalone enrichi avec les types étendus */
export interface StandaloneQuote extends Quote {
  quote_type: "messaging" | "standalone";
  public_token: string;
  artisan_name?: string | null;
  artisan_email?: string | null;
  artisan_phone?: string | null;
  artisan_address?: string | null;
  artisan_activity?: string | null;
  client_name?: string | null;
  client_email?: string | null;
  client_phone?: string | null;
  client_address?: string | null;
}

export function useStandaloneQuotes() {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState<StandaloneQuote[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("quotes")
        .select("*")
        .eq("artisan_user_id", user.id)
        .eq("quote_type", "standalone")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setQuotes((data ?? []) as StandaloneQuote[]);
    } catch (e: any) {
      toast.error("Impossible de charger les devis");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Sauvegarde un devis standalone en base via la RPC atomique.
   * Retourne l'ID du devis créé, ou null en cas d'erreur.
   */
  const saveQuote = useCallback(
    async (form: StandaloneDevisFormData): Promise<string | null> => {
      if (!user) return null;
      setSaving(true);
      try {
        const { data, error } = await supabase.rpc("save_standalone_quote" as any, {
          p_title: form.title,
          p_description: form.description,
          p_total_amount: form.totalAmount,
          p_valid_until: new Date(form.validUntil).toISOString(),
          p_estimated_intervention_delay: form.estimatedDelay ?? null,
          p_notes: form.notes ?? null,
          p_currency: form.currency ?? "DZD",
          p_artisan_name: form.artisan.name || null,
          p_artisan_email: form.artisan.email || null,
          p_artisan_phone: form.artisan.phone || null,
          p_artisan_address: form.artisan.address || null,
          p_artisan_activity: form.artisan.activity || null,
          p_client_name: form.client.name || null,
          p_client_email: form.client.email || null,
          p_client_phone: form.client.phone || null,
          p_client_address: form.client.address || null,
          p_line_items: form.lineItems.map((li) => ({
            label: li.label,
            description: li.description ?? null,
            quantity: li.quantity,
            unit: li.unit,
            unit_price: li.unit_price,
            total_price: li.total_price,
          })),
        });

        if (error) throw error;
        toast.success("Devis sauvegardé !");
        await load();
        return data as string;
      } catch (e: any) {
        toast.error(e.message || "Impossible de sauvegarder le devis");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [user, load]
  );

  /** Récupère un devis standalone avec ses lignes */
  const getQuoteById = useCallback(
    async (id: string): Promise<StandaloneQuote | null> => {
      const { data, error } = await supabase
        .from("quotes")
        .select("*, quote_line_items(*)")
        .eq("id", id)
        .eq("artisan_user_id", user?.id ?? "")
        .maybeSingle();

      if (error || !data) return null;
      const q = data as any;
      return { ...q, line_items: q.quote_line_items ?? [] } as StandaloneQuote;
    },
    [user]
  );

  return { quotes, loading, saving, saveQuote, getQuoteById, reload: load };
}
